import { ReactNode, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArtBox, ArtDialog, ArtStack, ArtTypography } from "@art/shared-ui";
import { faCheck, faTh } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import FaIcon from "../../utils/fa_icon";
import { productList } from "../menu-list";
import { useAppSwitch } from "./app-switch";
import AppTile from "./app-tile";
import { SwitchableApp } from "./app-switch.types";
import styles from "./app-switch.module.scss";
import { filterAccessibleProducts } from "./product-access";
import { usePermission, usePermissionsContext } from "../../../global/permissions";
import { applicationBrandForPath } from "../../../utils/application-brand";

interface AppSwitcherProps {
  userRole?: { name: string }[];
  renderTrigger?: (props: {
    open: () => void;
    isOpen: boolean;
    activeApp?: any;
  }) => ReactNode;
}

export default function AppSwitcher({
  userRole: _userRole,
  renderTrigger,
}: AppSwitcherProps) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { switchApp } = useAppSwitch();
  const canAccessSuperAdmin = usePermission("GENERAL", "MASTER_API", "Auth");
  const { has } = usePermissionsContext();
  const apps = useMemo(
    () => filterAccessibleProducts(productList, canAccessSuperAdmin, has),
    [canAccessSuperAdmin, has],
  );

  const routeApp = apps.find((app) =>
    location.pathname.includes(`/${app.prefix}`),
  );
  const { applicationCode } = applicationBrandForPath(location.pathname);
  const activeApp = routeApp || (
    applicationCode
      ? apps.find(
          (app) => app.applicationCode === applicationCode,
        )
      : undefined
  );

  const close = () => {
    setOpen(false);
  };

  const handleSelect = (app: any) => {
    close();
    if (app.key === activeApp?.key) return;
    switchApp(app as SwitchableApp);
  };

  return (
    <>
      {renderTrigger ? (
        renderTrigger({ open: () => setOpen(true), isOpen: open, activeApp })
      ) : (
        <button
          type="button"
          className={classNames(styles.trigger, {
            [styles.trigger_open]: open,
          })}
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={
            activeApp
              ? `Switch application, current: ${activeApp.title}`
              : "Switch application"
          }
          data-qa="header-product-switcher"
        >
          <FaIcon icon={faTh} className={styles.trigger_icon} />
          {activeApp && (
            <span
              className={styles.trigger_dot}
              style={{ ["--accent" as string]: activeApp.accent }}
            />
          )}
        </button>
      )}

      <ArtDialog
        open={open}
        onClose={close}
        title="Applications"
        size="medium"
        dataQa="app-switcher-popup"
      >
        <AppList
          activeApp={activeApp}
          apps={apps}
          onSelect={handleSelect}
        />
      </ArtDialog>
    </>
  );
}

function AppList({
  activeApp,
  apps,
  onSelect,
}: {
  activeApp?: any;
  apps: any[];
  onSelect: (app: any) => void;
}) {
  const [previewAppKey, setPreviewAppKey] = useState<string | null>(null);
  const previewApp = apps.find((app) => app.key === previewAppKey);
  const showcaseApp = previewApp || activeApp || apps[0];

  return (
    <ArtStack className={styles.popup_body}>
      <ArtBox className={styles.launcher_layout}>
        {apps.length ? (
          <ArtBox className={styles.app_grid}>
            {apps.map((app: any, index: number) => {
              const isActive = app.key === activeApp?.key;
              return (
                <button
                  key={app.key}
                  type="button"
                  className={classNames(styles.app_card, {
                    [styles.app_card_active]: isActive,
                  })}
                  style={{
                    ["--accent" as string]: app.accent,
                    ["--row-index" as string]: index,
                  }}
                  aria-label={
                    isActive
                      ? `${app.title}, current application`
                      : `Open ${app.title}`
                  }
                  data-qa={`drop-menu-${app.key}`}
                  onClick={() => onSelect(app)}
                  onMouseEnter={() => setPreviewAppKey(app.key)}
                  onMouseLeave={() => setPreviewAppKey(null)}
                  onFocus={() => setPreviewAppKey(app.key)}
                  onBlur={() => setPreviewAppKey(null)}
                >
                  <span className={styles.app_card_header}>
                    <AppTile app={app} className={styles.app_card_icon} />
                    {isActive && (
                      <span
                        className={styles.current_badge}
                        title="Current application"
                      >
                        <FaIcon icon={faCheck} />
                        <span className={styles.current_badge_text}>
                          Current
                        </span>
                      </span>
                    )}
                  </span>
                  <span className={styles.app_card_content}>
                    <span className={styles.app_card_title}>{app.title}</span>
                  </span>
                </button>
              );
            })}
          </ArtBox>
        ) : (
          <ArtBox className={styles.empty_state}>
            <ArtTypography variant="subtitle1" sx={{ fontWeight: 700 }}>
              No applications available
            </ArtTypography>
            <ArtTypography variant="body2" color="text.secondary">
              No applications are assigned to your role.
            </ArtTypography>
          </ArtBox>
        )}

        <CurrentAppShowcase
          app={showcaseApp}
          isCurrent={showcaseApp?.key === activeApp?.key}
        />
      </ArtBox>
    </ArtStack>
  );
}

function CurrentAppShowcase({
  app,
  isCurrent,
}: {
  app?: any;
  isCurrent: boolean;
}) {
  const [activeSlide, setActiveSlide] = useState(0);
  const slides = useMemo(
    () =>
      app
        ? [
            {
              eyebrow: isCurrent ? "Current application" : "Application preview",
              title: app.tagline || app.title,
              body: app.description,
            },
            {
              eyebrow: `Inside ${app.title}`,
              title: app.readyLabel || "Your workspace is ready",
              body:
                app.stages?.[1] ||
                "Everything you need stays organized in one focused workspace.",
            },
            {
              eyebrow: "Switch with confidence",
              title: "Your work stays available",
              body:
                "Your current workspace and session remain available when you return.",
            },
          ]
        : [],
    [app, isCurrent],
  );

  useEffect(() => {
    setActiveSlide(0);
    if (slides.length < 2) return;
    const interval = window.setInterval(
      () => setActiveSlide((current) => (current + 1) % slides.length),
      4200,
    );
    return () => window.clearInterval(interval);
  }, [slides]);

  if (!app || !slides.length) return null;
  const slide = slides[activeSlide];

  return (
    <ArtBox
      className={styles.current_app_panel}
      style={{
        ["--accent" as string]: app.accent,
        ["--accent-alt" as string]: app.accentAlt || app.accent,
      }}
      role="region"
      aria-roledescription="carousel"
      aria-label={`${app.title} overview`}
    >
      <span className={styles.current_app_glow} aria-hidden="true" />
      <ArtBox className={styles.current_app_identity}>
        <AppTile app={app} className={styles.current_app_icon} />
        <ArtBox>
          <ArtTypography className={styles.current_app_label}>
            {isCurrent ? "YOU ARE WORKING IN" : "PREVIEWING"}
          </ArtTypography>
          <ArtTypography className={styles.current_app_name}>
            {app.title}
          </ArtTypography>
        </ArtBox>
      </ArtBox>

      <ArtBox
        key={activeSlide}
        className={styles.current_app_slide}
        aria-live="polite"
      >
        <ArtTypography className={styles.slide_eyebrow}>
          {slide.eyebrow}
        </ArtTypography>
        <ArtTypography className={styles.slide_title}>
          {slide.title}
        </ArtTypography>
        <ArtTypography className={styles.slide_body}>{slide.body}</ArtTypography>
      </ArtBox>

      <ArtBox className={styles.slider_controls} aria-label="Overview slides">
        {slides.map((item, index) => (
          <button
            key={item.eyebrow}
            type="button"
            className={classNames(styles.slider_dot, {
              [styles.slider_dot_active]: index === activeSlide,
            })}
            aria-label={`Show slide ${index + 1}`}
            aria-current={index === activeSlide ? "true" : undefined}
            onClick={() => setActiveSlide(index)}
          />
        ))}
      </ArtBox>
    </ArtBox>
  );
}
